import React, { useState, useEffect } from 'react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { employeeAPI, paymentsAPI, notificationsAPI } from '../../services/api';
import { toast } from 'sonner';
import { Mail, Lock, Bell, Crown, Loader2, ExternalLink, Trash2, EyeOff, Plus, X, Globe, Users, Eye, Shield, CheckCircle, Building2, AlertTriangle, Download } from 'lucide-react';

const Settings = () => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [notifications, setNotifications] = useState({
    record_submitted: true,
    profile_viewed: true,
    new_message: true,
  });
  const [dailyDigestEnabled, setDailyDigestEnabled] = useState(true);
  const [savingDigest, setSavingDigest] = useState(false);
  const [upgrading, setUpgrading] = useState(false);
  
  // Hide status feature
  const [hideStatusEnabled, setHideStatusEnabled] = useState(false);
  const [hiddenDomains, setHiddenDomains] = useState([]);
  const [domainInput, setDomainInput] = useState('');
  const [savingDomains, setSavingDomains] = useState(false);
  
  // Profile visibility
  const [profileVisibility, setProfileVisibility] = useState('open');
  const [savingVisibility, setSavingVisibility] = useState(false);
  const [accessApprovals, setAccessApprovals] = useState([]);
  const [showResetModal, setShowResetModal] = useState(false);
  const [resettingPermissions, setResettingPermissions] = useState(false);
  
  // FCRA Data File
  const [downloadingDataFile, setDownloadingDataFile] = useState(false);

  useEffect(() => {
    fetchProfile();
    fetchNotificationSettings();
    fetchVisibilitySettings();
  }, []);

  const fetchProfile = async () => {
    try {
      const res = await employeeAPI.getProfile();
      setProfile(res.data);
      setHideStatusEnabled(res.data.hide_status_enabled || false);
      setHiddenDomains(res.data.hidden_employer_domains || []);
    } catch (error) {
      console.error('Error fetching profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchVisibilitySettings = async () => {
    try {
      const [visibilityRes, approvalsRes] = await Promise.all([
        employeeAPI.getProfileVisibility(),
        employeeAPI.getAccessApprovals()
      ]);
      setProfileVisibility(visibilityRes.data.visibility || 'open');
      setAccessApprovals(approvalsRes.data.approvals || []);
    } catch (error) {
      console.error('Error fetching visibility settings:', error);
    }
  };

  const handleVisibilityChange = async (newVisibility) => {
    setSavingVisibility(true);
    try {
      await employeeAPI.updateProfileVisibility(newVisibility);
      setProfileVisibility(newVisibility);
      toast.success(`Profile visibility set to ${newVisibility}`);
    } catch (error) {
      toast.error('Failed to update visibility setting');
    } finally {
      setSavingVisibility(false);
    }
  };

  const handleRevokeAccess = async (employerId) => {
    try {
      await employeeAPI.revokeAccessApproval(employerId);
      setAccessApprovals(accessApprovals.filter(a => a.employer_id !== employerId));
      toast.success('Access revoked');
    } catch (error) {
      toast.error('Failed to revoke access');
    }
  };

  const handleResetAllPermissions = async () => {
    setResettingPermissions(true);
    try {
      await employeeAPI.resetAccessPermissions();
      setAccessApprovals([]);
      setShowResetModal(false);
      toast.success('All access permissions have been reset');
    } catch (error) {
      toast.error('Failed to reset permissions');
    } finally {
      setResettingPermissions(false);
    }
  };

  const fetchNotificationSettings = async () => {
    try {
      const res = await notificationsAPI.getSettings();
      setDailyDigestEnabled(res.data.daily_digest_enabled !== false);
    } catch (error) {
      console.error('Error fetching notification settings:', error);
    }
  };

  const handleToggleDailyDigest = async (enabled) => {
    setSavingDigest(true);
    try {
      await notificationsAPI.updateSettings({ daily_digest_enabled: enabled });
      setDailyDigestEnabled(enabled);
      toast.success(enabled ? 'Daily digest enabled' : 'Daily digest disabled');
    } catch (error) {
      toast.error('Failed to update notification settings');
    } finally {
      setSavingDigest(false);
    }
  };

  const handleUpgradeToPro = async () => {
    setUpgrading(true);
    try {
      const res = await paymentsAPI.createCheckout('employee_pro', window.location.origin);
      window.location.href = res.data.url;
    } catch (error) {
      toast.error('Failed to start checkout');
      setUpgrading(false);
    }
  };

  const handleAddDomain = () => {
    const domain = domainInput.trim().toLowerCase().replace(/^@/, '');
    if (!domain) return;
    
    // Simple domain validation
    if (!domain.includes('.') || domain.includes(' ')) {
      toast.error('Please enter a valid domain (e.g., company.com)');
      return;
    }
    
    if (hiddenDomains.includes(domain)) {
      toast.error('Domain already added');
      return;
    }
    
    if (hiddenDomains.length >= 3) {
      toast.error('Maximum 3 domains allowed');
      return;
    }
    
    setHiddenDomains([...hiddenDomains, domain]);
    setDomainInput('');
  };

  const handleRemoveDomain = (domain) => {
    setHiddenDomains(hiddenDomains.filter(d => d !== domain));
  };

  const handleSaveHiddenDomains = async () => {
    setSavingDomains(true);
    try {
      await employeeAPI.updateProfile({
        hide_status_enabled: hideStatusEnabled,
        hidden_employer_domains: hiddenDomains
      });
      toast.success('Privacy settings saved');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSavingDomains(false);
    }
  };

  const handleToggleHideStatus = async (enabled) => {
    if (enabled && !profile?.is_pro) {
      toast.error('This is a Pro feature. Upgrade to enable.');
      return;
    }
    setHideStatusEnabled(enabled);
  };

  const handleDownloadDataFile = async () => {
    setDownloadingDataFile(true);
    try {
      const response = await employeeAPI.getFcraDataFile();
      const data = response.data;
      
      // Create and download the JSON file
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const today = new Date().toISOString().split('T')[0];
      a.download = `vett-data-${today}.json`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success('Data file downloaded successfully');
    } catch (error) {
      console.error('Error downloading data file:', error);
      toast.error('Could not generate your data file. Please try again.');
    } finally {
      setDownloadingDataFile(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-[#1B5AFF]" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-2xl space-y-8" data-testid="employee-settings">
        {/* Header */}
        <div className="animate-fade-in">
          <h1 className="text-3xl font-bold text-[#0A0F1E]">Settings</h1>
          <p className="mt-2 text-[#5B6472]">Manage your account preferences.</p>
        </div>

        {/* Pro Upgrade */}
        <Card className={`border rounded-xl p-6 animate-fade-in ${
          profile?.is_pro 
            ? 'bg-gradient-to-r from-[#1B5AFF]/10 to-[#FFFFFF] border-[#1B5AFF]/30' 
            : 'bg-[#FFFFFF] border-[#E8EDF5]'
        }`}>
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-4">
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                profile?.is_pro ? 'bg-[#1B5AFF]/20' : 'bg-[#F5F8FF]'
              }`}>
                <Crown className={`w-6 h-6 ${profile?.is_pro ? 'text-[#1B5AFF]' : 'text-[#5B6472]'}`} />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-[#0A0F1E]">
                  {profile?.is_pro ? 'Pro Member' : 'Upgrade to Pro'}
                </h2>
                <p className="text-[#5B6472] text-sm mt-1">
                  {profile?.is_pro 
                    ? 'You have access to all Pro features' 
                    : 'Lock your profile so only approved employers can view your full record'}
                </p>
              </div>
            </div>
            {!profile?.is_pro && (
              <Button
                onClick={handleUpgradeToPro}
                disabled={upgrading}
                className="bg-[#1B5AFF] hover:bg-[#1B5AFF] text-[#FFFFFF] rounded-xl btn-glow"
                data-testid="upgrade-pro-btn"
              >
                {upgrading ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : null}
                €9.90/month
              </Button>
            )}
          </div>
          {profile?.is_pro && (
            <div className="mt-4 pt-4 border-t border-[#E8EDF5]">
              <p className="text-[#5B6472] text-sm">
                Next billing date: {new Date(Date.now() + 30*24*60*60*1000).toLocaleDateString()}
              </p>
              <Button variant="ghost" className="mt-2 text-red-400 hover:text-red-300 hover:bg-red-500/10 p-0 h-auto">
                Cancel subscription
              </Button>
            </div>
          )}
        </Card>

        {/* Profile Visibility */}
        <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6 animate-fade-in" data-testid="visibility-settings">
          <div className="flex items-start gap-3 mb-6">
            <div className="w-10 h-10 rounded-xl bg-[#F5F8FF] flex items-center justify-center">
              <Eye className="w-5 h-5 text-[#1B5AFF]" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-[#0A0F1E]">Profile Visibility</h2>
              <p className="text-[#5B6472] text-sm mt-1">
                Control who can see your verified work records
              </p>
            </div>
          </div>

          <div className="space-y-3">
            {/* Open */}
            <button
              onClick={() => handleVisibilityChange('open')}
              disabled={savingVisibility}
              className={`w-full p-4 rounded-xl border text-left transition-all ${
                profileVisibility === 'open'
                  ? 'bg-green-500/10 border-green-500/50'
                  : 'bg-[#F5F8FF]/30 border-[#E8EDF5] hover:bg-[#F5F8FF]/50'
              }`}
              data-testid="visibility-open"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Globe className={`w-5 h-5 ${profileVisibility === 'open' ? 'text-green-400' : 'text-[#5B6472]'}`} />
                  <div>
                    <div className="flex items-center gap-2">
                      <span className={`font-medium ${profileVisibility === 'open' ? 'text-green-400' : 'text-[#0A0F1E]'}`}>
                        Open
                      </span>
                      {profileVisibility === 'open' && (
                        <Badge className="bg-green-500/20 text-green-400 border-0 text-xs">Active</Badge>
                      )}
                    </div>
                    <p className="text-[#5B6472] text-sm mt-0.5">
                      Full profile visible to any verified employer with an active VETT account
                    </p>
                  </div>
                </div>
                {profileVisibility === 'open' && <CheckCircle className="w-5 h-5 text-green-400" />}
              </div>
            </button>

            {/* Selective */}
            <button
              onClick={() => handleVisibilityChange('selective')}
              disabled={savingVisibility}
              className={`w-full p-4 rounded-xl border text-left transition-all ${
                profileVisibility === 'selective'
                  ? 'bg-blue-500/10 border-blue-500/50'
                  : 'bg-[#F5F8FF]/30 border-[#E8EDF5] hover:bg-[#F5F8FF]/50'
              }`}
              data-testid="visibility-selective"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Users className={`w-5 h-5 ${profileVisibility === 'selective' ? 'text-blue-400' : 'text-[#5B6472]'}`} />
                  <div>
                    <div className="flex items-center gap-2">
                      <span className={`font-medium ${profileVisibility === 'selective' ? 'text-blue-400' : 'text-[#0A0F1E]'}`}>
                        Selective
                      </span>
                      {profileVisibility === 'selective' && (
                        <Badge className="bg-blue-500/20 text-blue-400 border-0 text-xs">Active</Badge>
                      )}
                    </div>
                    <p className="text-[#5B6472] text-sm mt-0.5">
                      Employers see basic info; full records require your approval
                    </p>
                  </div>
                </div>
                {profileVisibility === 'selective' && <CheckCircle className="w-5 h-5 text-blue-400" />}
              </div>
            </button>

            {/* Private */}
            <button
              onClick={() => handleVisibilityChange('private')}
              disabled={savingVisibility}
              className={`w-full p-4 rounded-xl border text-left transition-all ${
                profileVisibility === 'private'
                  ? 'bg-amber-500/10 border-amber-500/50'
                  : 'bg-[#F5F8FF]/30 border-[#E8EDF5] hover:bg-[#F5F8FF]/50'
              }`}
              data-testid="visibility-private"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Shield className={`w-5 h-5 ${profileVisibility === 'private' ? 'text-amber-400' : 'text-[#5B6472]'}`} />
                  <div>
                    <div className="flex items-center gap-2">
                      <span className={`font-medium ${profileVisibility === 'private' ? 'text-amber-400' : 'text-[#0A0F1E]'}`}>
                        Private
                      </span>
                      {profileVisibility === 'private' && (
                        <Badge className="bg-amber-500/20 text-amber-400 border-0 text-xs">Active</Badge>
                      )}
                    </div>
                    <p className="text-[#5B6472] text-sm mt-0.5">
                      Profile hidden from all searches. Build your ledger privately
                    </p>
                  </div>
                </div>
                {profileVisibility === 'private' && <CheckCircle className="w-5 h-5 text-amber-400" />}
              </div>
            </button>
          </div>

          {/* Selective visibility - access approvals */}
          {profileVisibility === 'selective' && accessApprovals.length > 0 && (
            <div className="mt-6 pt-6 border-t border-[#E8EDF5]">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-[#0A0F1E] font-medium">Employers with Full Access</h3>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowResetModal(true)}
                  className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                >
                  Reset All
                </Button>
              </div>
              <div className="space-y-2">
                {accessApprovals.map((approval) => (
                  <div
                    key={approval.employer_id}
                    className="flex items-center justify-between p-3 rounded-lg bg-[#F5F8FF]/30 border border-[#E8EDF5]"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-[#F5F8FF] flex items-center justify-center">
                        <Building2 className="w-4 h-4 text-[#5B6472]" />
                      </div>
                      <div>
                        <p className="text-[#0A0F1E] text-sm">{approval.company_name}</p>
                        <p className="text-[#5B6472] text-xs">
                          Approved {new Date(approval.approved_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRevokeAccess(approval.employer_id)}
                      className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                    >
                      Revoke
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Card>

        {/* Hide Opportunity Status - Pro Feature */}
        <Card className={`border rounded-xl p-6 animate-fade-in ${
          profile?.is_pro 
            ? 'bg-[#FFFFFF] border-[#E8EDF5]' 
            : 'bg-[#FFFFFF]/50 border-[#E8EDF5]/50'
        }`} data-testid="hide-status-card">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-start gap-3">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                profile?.is_pro ? 'bg-[#F5F8FF]' : 'bg-[#F5F8FF]/50'
              }`}>
                <EyeOff className={`w-5 h-5 ${profile?.is_pro ? 'text-[#1B5AFF]' : 'text-[#5B6472]'}`} />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h2 className={`text-lg font-semibold ${profile?.is_pro ? 'text-[#0A0F1E]' : 'text-[#5B6472]'}`}>
                    Hide Opportunity Status
                  </h2>
                  {!profile?.is_pro && (
                    <span className="px-2 py-0.5 rounded-full bg-[#1B5AFF]/20 text-[#1B5AFF] text-xs font-medium">
                      Pro
                    </span>
                  )}
                </div>
                <p className="text-[#5B6472] text-sm mt-1">
                  Hide your job search status from specific employers
                </p>
              </div>
            </div>
            <Switch
              checked={hideStatusEnabled}
              onCheckedChange={handleToggleHideStatus}
              disabled={!profile?.is_pro}
              data-testid="hide-status-toggle"
            />
          </div>

          {!profile?.is_pro ? (
            <div className="bg-[#F5F8FF]/30 rounded-xl p-4 border border-[#E8EDF5]">
              <p className="text-[#5B6472] text-sm mb-3">
                Hide your job search status from your current employer. Upgrade to Pro.
              </p>
              <Button
                onClick={handleUpgradeToPro}
                disabled={upgrading}
                size="sm"
                className="bg-[#1B5AFF] hover:bg-[#1B5AFF] text-[#FFFFFF] rounded-lg"
              >
                Upgrade to Pro
              </Button>
            </div>
          ) : hideStatusEnabled ? (
            <div className="space-y-4">
              <div>
                <Label className="text-[#0A0F1E] text-sm">
                  Enter employer email domains to hide your status from (max 3):
                </Label>
                <div className="flex gap-2 mt-2">
                  <Input
                    value={domainInput}
                    onChange={(e) => setDomainInput(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddDomain())}
                    placeholder="e.g., company.com"
                    className="flex-1 bg-[#F5F8FF]/50 border-[#E8EDF5] text-[#0A0F1E] rounded-xl h-10"
                    disabled={hiddenDomains.length >= 3}
                  />
                  <Button
                    onClick={handleAddDomain}
                    disabled={hiddenDomains.length >= 3}
                    className="bg-[#F5F8FF] hover:bg-[#F5F8FF]/80 text-[#0A0F1E] rounded-xl"
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              {hiddenDomains.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {hiddenDomains.map((domain) => (
                    <span
                      key={domain}
                      className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-[#F5F8FF] text-[#0A0F1E] text-sm border border-[#E8EDF5]"
                    >
                      @{domain}
                      <button
                        onClick={() => handleRemoveDomain(domain)}
                        className="text-[#5B6472] hover:text-red-400"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}

              <p className="text-[#5B6472] text-xs">
                Employers using emails from these domains won't see your "Open to Opportunities" status.
              </p>

              <Button
                onClick={handleSaveHiddenDomains}
                disabled={savingDomains}
                className="bg-[#1B5AFF] hover:bg-[#1B5AFF] text-[#FFFFFF] rounded-xl"
              >
                {savingDomains && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Save Settings
              </Button>
            </div>
          ) : (
            <p className="text-[#5B6472] text-sm">
              Enable this feature to add employer domains.
            </p>
          )}
        </Card>

        {/* Email */}
        <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6 animate-fade-in">
          <div className="flex items-center gap-3 mb-4">
            <Mail className="w-5 h-5 text-[#1B5AFF]" />
            <h2 className="text-lg font-semibold text-[#0A0F1E]">Email Address</h2>
          </div>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-[#0A0F1E]">Current Email</Label>
              <Input
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="new@email.com"
                className="bg-[#F5F8FF]/50 border-[#E8EDF5] text-[#0A0F1E] rounded-xl h-12"
              />
            </div>
            <Button className="bg-[#F5F8FF] hover:bg-[#F5F8FF]/80 text-[#0A0F1E] rounded-xl">
              Update Email
            </Button>
          </div>
        </Card>

        {/* Password */}
        <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6 animate-fade-in">
          <div className="flex items-center gap-3 mb-4">
            <Lock className="w-5 h-5 text-[#1B5AFF]" />
            <h2 className="text-lg font-semibold text-[#0A0F1E]">Change Password</h2>
          </div>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-[#0A0F1E]">Current Password</Label>
              <Input
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                className="bg-[#F5F8FF]/50 border-[#E8EDF5] text-[#0A0F1E] rounded-xl h-12"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-[#0A0F1E]">New Password</Label>
              <Input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="bg-[#F5F8FF]/50 border-[#E8EDF5] text-[#0A0F1E] rounded-xl h-12"
              />
            </div>
            <Button className="bg-[#F5F8FF] hover:bg-[#F5F8FF]/80 text-[#0A0F1E] rounded-xl">
              Update Password
            </Button>
          </div>
        </Card>

        {/* Notifications */}
        <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6 animate-fade-in" data-testid="notifications-card">
          <div className="flex items-center gap-3 mb-6">
            <Bell className="w-5 h-5 text-[#1B5AFF]" />
            <h2 className="text-lg font-semibold text-[#0A0F1E]">Email Notifications</h2>
          </div>
          <div className="space-y-4">
            {/* Daily Digest Toggle */}
            <div className="flex items-center justify-between py-2 border-b border-[#E8EDF5] pb-4 mb-4">
              <div>
                <span className="text-[#0A0F1E] font-medium">Daily Summary Digest</span>
                <p className="text-[#5B6472] text-sm mt-1">
                  Receive a daily email with unread messages, pending records, and upcoming reviews
                </p>
              </div>
              <Switch
                checked={dailyDigestEnabled}
                onCheckedChange={handleToggleDailyDigest}
                disabled={savingDigest}
                data-testid="daily-digest-toggle"
              />
            </div>
            
            {[
              { key: 'record_submitted', label: 'When an employer submits a record' },
              { key: 'profile_viewed', label: 'When an employer views my profile' },
              { key: 'new_message', label: 'When I receive a new message' },
            ].map((item) => (
              <div key={item.key} className="flex items-center justify-between py-2">
                <span className="text-[#0A0F1E]">{item.label}</span>
                <Switch
                  checked={notifications[item.key]}
                  onCheckedChange={(checked) => setNotifications(prev => ({ ...prev, [item.key]: checked }))}
                />
              </div>
            ))}
          </div>
        </Card>

        {/* FCRA Data File Download */}
        <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6 animate-fade-in" data-testid="fcra-data-file-card">
          <div className="flex items-center gap-3 mb-4">
            <Download className="w-5 h-5 text-[#1B5AFF]" />
            <h2 className="text-lg font-semibold text-[#0A0F1E]">Your VETT Data File</h2>
          </div>
          <p className="text-[#5B6472] text-sm mb-4">
            FCRA requires that you can access all information VETT holds about you. This file includes your profile, all VETT records, your VETT Score history, and a log of which employers have accessed your data.
          </p>
          <Button 
            onClick={handleDownloadDataFile}
            disabled={downloadingDataFile}
            className="bg-[#F5F8FF] hover:bg-[#F5F8FF]/80 text-[#0A0F1E] rounded-xl"
            data-testid="download-data-file-btn"
          >
            {downloadingDataFile ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Generating...
              </>
            ) : (
              <>
                <Download className="mr-2 h-4 w-4" />
                Download My VETT Data File
              </>
            )}
          </Button>
        </Card>

        {/* Delete Account */}
        <Card className="bg-[#FFFFFF] border border-red-500/20 rounded-xl p-6 animate-fade-in">
          <div className="flex items-center gap-3 mb-4">
            <Trash2 className="w-5 h-5 text-red-400" />
            <h2 className="text-lg font-semibold text-red-400">Delete Account</h2>
          </div>
          <p className="text-[#5B6472] text-sm mb-4">
            Once you delete your account, all your data and records will be permanently removed.
          </p>
          <Button variant="outline" className="border-red-500/30 text-red-400 hover:bg-red-500/10 rounded-xl">
            Delete Account
          </Button>
        </Card>
      </div>

      {/* Reset Permissions Modal */}
      <Dialog open={showResetModal} onOpenChange={setShowResetModal}>
        <DialogContent className="bg-[#FFFFFF] border-[#E8EDF5] text-[#0A0F1E] sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-400" />
              Reset All Access Permissions
            </DialogTitle>
            <DialogDescription className="text-[#5B6472]">
              This will revoke full record access from all employers who currently have it.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <div className="p-4 rounded-xl bg-amber-500/10 border border-amber-500/30">
              <p className="text-[#0A0F1E] text-sm">
                <strong>{accessApprovals.length}</strong> employer{accessApprovals.length !== 1 ? 's' : ''} will lose access to your full records. They will need to request access again if they want to see your complete profile.
              </p>
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setShowResetModal(false)}
              className="border-[#E8EDF5] text-[#0A0F1E]"
            >
              Cancel
            </Button>
            <Button
              onClick={handleResetAllPermissions}
              disabled={resettingPermissions}
              className="bg-red-500 hover:bg-red-600 hover:bg-red-700 text-[#FFFFFF] rounded-xl"
            >
              {resettingPermissions ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Resetting...
                </>
              ) : (
                'Reset All'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

export default Settings;
